/**
 * 项目表单组件
 * Feature: mobile-responsive-ui-adaptation
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input, DatePicker, Select, message, InputNumber, Slider, Row, Col, Tag, Tooltip, Divider, Space, Typography, Card, AutoComplete, Alert } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Project, ProjectStatus, BudgetBreakdown, BudgetMode, RevenueBreakdown } from '../../types';
import type { ProjectSuggestion } from '../../services/projectService';
import { ProjectService, CreateProjectData, UpdateProjectData } from '../../services/projectService';
import BudgetBreakdownInput from '../../components/BudgetBreakdownInput';
import { useResponsiveConfig } from '../../responsive/hooks/useResponsiveConfig';
import { MobileForm } from '../../responsive/components/MobileForm';

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

interface ProjectFormProps {
  project?: Project | null;
  onSubmit: () => void;
  onCancel: () => void;
}

const DEFAULT_REVENUE_BREAKDOWN: RevenueBreakdown = {
  contract: 0,
  changeOrder: 0,
  claim: 0,
  settlement: 0,
  other: 0,
};

const ProjectForm: React.FC<ProjectFormProps> = ({
  project,
  onSubmit,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [budgetValue, setBudgetValue] = useState<number>(0);
  const [budgetMode, setBudgetMode] = useState<BudgetMode>(BudgetMode.AUTO);
  const [budgetLocked, setBudgetLocked] = useState<boolean>(false);
  const [revenueTotal, setRevenueTotal] = useState<number>(0);
  const [nameKeyword, setNameKeyword] = useState('');
  const [nameOptions, setNameOptions] = useState<Array<{ value: string; label: React.ReactNode }>>([]);
  const [nameMatches, setNameMatches] = useState<ProjectSuggestion[]>([]);
  const { isMobile } = useResponsiveConfig();

  useEffect(() => {
    if (project) {
      form.setFieldsValue({
        name: project.name,
        projectCode: project.projectCode,
        description: project.description,
        startDate: project.startDate ? dayjs(project.startDate) : null,
        endDate: project.endDate ? dayjs(project.endDate) : null,
        status: project.status,
        budget: project.budget || 0,
        budgetBreakdown: project.budgetBreakdown || {
          labor: 0,
          equipment: 0,
          material: 0,
          subcontract: 0,
          coordination: 0,
          contingency: 0
        },
        revenueBreakdown: project.revenueBreakdown || DEFAULT_REVENUE_BREAKDOWN,
        budgetVersionReason: '',
        progress: project.progress || 0,
      });
      setBudgetValue(project.budget || 0);
      setBudgetMode(project.budgetMode || BudgetMode.AUTO);
      setBudgetLocked(project.budgetLocked || false);
      setRevenueTotal(project.revenueTotal || 0);
      setNameKeyword(project.name || '');
    } else {
      form.resetFields();
      setBudgetValue(0);
      setBudgetMode(BudgetMode.AUTO);
      setBudgetLocked(false);
      setRevenueTotal(0);
      setNameKeyword('');
    }
    setNameOptions([]);
    setNameMatches([]);
  }, [project, form]);

  useEffect(() => {
    if (project) {
      return;
    }

    const keyword = nameKeyword.trim();
    if (!keyword) {
      setNameOptions([]);
      setNameMatches([]);
      return;
    }

    const timer = window.setTimeout(async () => {
      try {
        const response = await ProjectService.getProjectSuggestions(keyword, 8);
        if (!response.success || !response.data) {
          setNameOptions([]);
          setNameMatches([]);
          return;
        }

        const suggestions = response.data.filter((item) => item._id !== project?._id);
        setNameMatches(suggestions);
        setNameOptions(
          suggestions.map((item) => ({
            value: item.name,
            label: (
              <Space orientation="vertical" size={0} style={{ lineHeight: 1.25 }}>
                <Text strong>{item.name}</Text>
                <Space size={8} wrap>
                  <Text type="secondary">{item.projectCode || '无编码'}</Text>
                  <Tag
                    color={item.status === ProjectStatus.ACTIVE ? 'green' : item.status === ProjectStatus.COMPLETED ? 'blue' : 'orange'}
                    style={{ marginInlineEnd: 0 }}
                  >
                    {item.status === ProjectStatus.ACTIVE ? '进行中' : item.status === ProjectStatus.COMPLETED ? '已完成' : '已暂停'}
                  </Tag>
                </Space>
              </Space>
            ),
          }))
        );
      } catch {
        setNameOptions([]);
        setNameMatches([]);
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [nameKeyword, project]);

  const duplicateCandidates = useMemo(() => {
    const keyword = nameKeyword.trim().toLowerCase();
    if (!keyword) {
      return [];
    }

    return nameMatches.filter((item) => item.name.trim().toLowerCase().includes(keyword));
  }, [nameKeyword, nameMatches]);

  const exactDuplicate = useMemo(() => {
    const keyword = nameKeyword.trim().toLowerCase();
    if (!keyword) {
      return undefined;
    }

    return duplicateCandidates.find((item) => item.name.trim().toLowerCase() === keyword);
  }, [nameKeyword, duplicateCandidates]);

  const handleBudgetChange = (value: number | null) => {
    setBudgetValue(value || 0);
    form.setFieldsValue({ budget: value || 0 });
  };

  const handleBudgetModeChange = (mode: BudgetMode) => {
    setBudgetMode(mode);
  };

  const handleBreakdownChange = (breakdown: BudgetBreakdown) => {
    form.setFieldsValue({ budgetBreakdown: breakdown });

    if (budgetMode === BudgetMode.AUTO) {
      const total = (breakdown.labor || 0) +
        (breakdown.equipment || 0) +
        (breakdown.material || 0) +
        (breakdown.subcontract || 0) +
        (breakdown.coordination || 0) +
        (breakdown.contingency || 0);
      setBudgetValue(total);
      form.setFieldsValue({ budget: total });
    }
  };

  const handleRevenueBreakdownChange = (key: keyof RevenueBreakdown, value: number | null) => {
    const current = {
      ...DEFAULT_REVENUE_BREAKDOWN,
      ...(form.getFieldValue('revenueBreakdown') || {}),
      [key]: value || 0,
    };
    form.setFieldsValue({ revenueBreakdown: current });
    const nextTotal = Object.values(current).reduce((sum, item) => sum + (Number(item) || 0), 0);
    setRevenueTotal(nextTotal);
  };

  const renderRevenueField = (label: string, field: keyof RevenueBreakdown) => (
    <Col xs={24} md={12} key={field}>
      <Form.Item label={label} name={['revenueBreakdown', field]}>
        <InputNumber
          style={{ width: '100%' }}
          min={0}
          precision={2}
          onChange={(value) => handleRevenueBreakdownChange(field, value)}
          disabled={budgetLocked}
          size={isMobile ? 'large' : 'middle'}
        />
      </Form.Item>
    </Col>
  );

  const renderReadOnlyRevenueField = (label: string, field: keyof RevenueBreakdown, suffix?: string) => (
    <Col xs={24} md={12} key={field}>
      <Form.Item
        label={
          <Space size={4}>
            <span>{label}</span>
            <Tooltip title="由增项管理自动汇总生成，无需手动录入">
              <Tag color="blue" style={{ fontSize: 11, marginInlineEnd: 0 }}>自动</Tag>
            </Tooltip>
          </Space>
        }
        name={['revenueBreakdown', field]}
      >
        <InputNumber
          style={{ width: '100%', background: '#f5f5f5' }}
          min={0}
          precision={2}
          value={form.getFieldValue('revenueBreakdown')?.[field] ?? 0}
          readOnly
          controls={false}
          formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          size={isMobile ? 'large' : 'middle'}
          addonAfter={suffix ? <span style={{ color: '#888' }}>{suffix}</span> : undefined}
        />
      </Form.Item>
    </Col>
  );

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      const formData = {
        name: values.name,
        projectCode: values.projectCode ? String(values.projectCode).trim().toUpperCase() : undefined,
        description: values.description,
        startDate: values.startDate?.format('YYYY-MM-DD'),
        endDate: values.endDate?.format('YYYY-MM-DD'),
        budget: values.budget || 0,
        budgetBreakdown: values.budgetBreakdown,
        revenueTotal: revenueTotal,
        revenueBreakdown: values.revenueBreakdown || DEFAULT_REVENUE_BREAKDOWN,
        budgetVersionReason: values.budgetVersionReason?.trim() || undefined,
        budgetMode: budgetMode,
        progress: values.progress || 0,
        ...(project && { status: values.status }),
      };

      let response;
      if (project) {
        response = await ProjectService.updateProject(project._id, formData as UpdateProjectData);
      } else {
        response = await ProjectService.createProject(formData as CreateProjectData);
      }

      if (response.success) {
        message.success(project ? '项目更新成功' : '项目创建成功');
        onSubmit();
      } else {
        message.error(response.message || (project ? '更新项目失败' : '创建项目失败'));
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        (typeof error === 'string' ? error : '') ||
        (project ? '更新项目失败' : '创建项目失败');
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <MobileForm
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      autoScrollToError
      keyboardAware
      submitButton={{
        text: project ? '更新' : '创建',
        loading: loading,
        disabled: budgetLocked,
        fixed: isMobile,
      }}
      cancelButton={{
        text: '取消',
        onClick: onCancel,
      }}
      initialValues={{
        status: ProjectStatus.ACTIVE,
        budget: 0,
        budgetBreakdown: {
          labor: 0,
          equipment: 0,
          material: 0,
          subcontract: 0,
          coordination: 0,
          contingency: 0
        },
        revenueBreakdown: DEFAULT_REVENUE_BREAKDOWN,
        budgetVersionReason: '',
        progress: 0,
      }}
    >
      <Divider titlePlacement="start" style={{ marginTop: 0 }}>基础信息</Divider>

      <Form.Item
        label="项目名称"
        name="name"
        rules={[
          { required: true, message: '请输入项目名称' },
          { max: 100, message: '项目名称不能超过100个字符' },
        ]}
        extra={!project ? '输入名称时会提示相近或同名项目，建议优先复用已有项目。' : undefined}
      >
        <AutoComplete
          options={!project ? nameOptions : []}
          onSearch={(value) => setNameKeyword(value)}
          onSelect={(value) => {
            form.setFieldsValue({ name: value });
            setNameKeyword(value);
          }}
          filterOption={false}
        >
          <Input
            placeholder="请输入项目名称"
            size={isMobile ? 'large' : 'middle'}
            onChange={(event) => setNameKeyword(event.target.value)}
          />
        </AutoComplete>
      </Form.Item>

      {!project && duplicateCandidates.length > 0 && (
        <Form.Item style={{ marginTop: -8 }}>
          <Alert
            type={exactDuplicate ? 'warning' : 'info'}
            showIcon
            title={exactDuplicate ? '发现同名项目，请确认是否重复创建' : '发现相近项目，请先确认是否已存在'}
            description={
              <Space size={[8, 8]} wrap>
                {duplicateCandidates.slice(0, 5).map((item) => (
                  <Tag key={item._id} color={exactDuplicate?._id === item._id ? 'red' : 'blue'}>
                    {item.name}{item.projectCode ? '（' + item.projectCode + '）' : ''}
                  </Tag>
                ))}
              </Space>
            }
          />
        </Form.Item>
      )}
      <Form.Item
        label="项目编码"
        name="projectCode"
        rules={[
          { max: 50, message: '项目编码不能超过50个字符' },
        ]}
      >
        <Input
          placeholder="请输入项目编码（可选，如 PRJ-2026-001）"
          size={isMobile ? 'large' : 'middle'}
        />
      </Form.Item>

      <Form.Item
        label="项目描述"
        name="description"
        rules={[
          { max: 500, message: '项目描述不能超过500个字符' },
        ]}
      >
        <TextArea
          rows={isMobile ? 4 : 3}
          placeholder="请输入项目描述（可选）"
          size={isMobile ? 'large' : 'middle'}
        />
      </Form.Item>

      <Row gutter={isMobile ? 12 : 16}>
        <Col xs={24} md={12}>
          <Form.Item
            label="开始日期"
            name="startDate"
            rules={[
              { required: true, message: '请选择开始日期' },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="请选择开始日期"
              size={isMobile ? 'large' : 'middle'}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="结束日期"
            name="endDate"
            rules={[
              {
                validator: (_, value) => {
                  const startDate = form.getFieldValue('startDate');
                  if (value && startDate && value.isBefore(startDate)) {
                    return Promise.reject(new Error('结束日期不能早于开始日期'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              placeholder="请选择结束日期（可选）"
              size={isMobile ? 'large' : 'middle'}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left">预算配置</Divider>

      <Form.Item
        label={
          <Space size={4} wrap>
            <span>项目预算（元）</span>
            {budgetLocked && (
              <Tag icon={<LockOutlined />} color="red" style={{ fontSize: isMobile ? 11 : 12 }}>
                已锁定
              </Tag>
            )}
            {budgetMode === BudgetMode.AUTO && (
              <Tooltip title="自动模式：总预算由预算成分自动计算">
                <Tag color="blue" style={{ fontSize: isMobile ? 11 : 12 }}>
                  自动计算
                </Tag>
              </Tooltip>
            )}
          </Space>
        }
        name="budget"
        tooltip="设置项目的总预算金额，用于成本控制和预警"
        rules={[
          { type: 'number', min: 0, message: '预算不能为负数' },
        ]}
      >
        <InputNumber
          style={{ width: '100%' }}
          placeholder="请输入项目预算"
          formatter={value => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={value => value!.replace(/¥\s?|(,*)/g, '')}
          min={0}
          step={1000}
          onChange={handleBudgetChange}
          disabled={budgetLocked || budgetMode === BudgetMode.AUTO}
          size={isMobile ? 'large' : 'middle'}
        />
      </Form.Item>

      <Form.Item
        name="budgetBreakdown"
        tooltip="设置预算成分明细，便于精确的成本控制和分析"
      >
        <BudgetBreakdownInput
          value={form.getFieldValue('budgetBreakdown')}
          onChange={handleBreakdownChange}
          totalBudget={budgetValue}
          onTotalBudgetChange={handleBudgetChange}
          budgetMode={budgetMode}
          onBudgetModeChange={handleBudgetModeChange}
          budgetLocked={budgetLocked}
          showModeSwitch={true}
          showLockStatus={true}
        />
      </Form.Item>

      <Card size="small" style={{ marginBottom: 16, background: '#fafcff', borderColor: '#d6e4ff' }}>
        <Space orientation="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Text strong>收入拆分</Text>
            <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
              用于生成经营摘要中的累计收入、预估利润与版本快照。其中增项收入由增项管理自动汇总。
            </Text>
          </div>
          <Row gutter={isMobile ? 12 : 16}>
            {renderRevenueField('合同收入', 'contract')}
            {renderReadOnlyRevenueField('增项收入', 'changeOrder')}
            {renderRevenueField('签证索赔', 'claim')}
            {renderRevenueField('结算调差', 'settlement')}
            {renderRevenueField('其他收入', 'other')}
            <Col xs={24} md={12}>
              <Form.Item label="累计收入汇总">
                <InputNumber
                  style={{ width: '100%' }}
                  value={revenueTotal}
                  readOnly
                  controls={false}
                  formatter={(value) => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  size={isMobile ? 'large' : 'middle'}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={project ? '本次版本说明' : '创建说明'}
            name="budgetVersionReason"
            tooltip="保存预算与收入后，会作为版本说明写入预算版本快照"
          >
            <TextArea rows={isMobile ? 3 : 2} maxLength={200} placeholder="例如：补录合同收入并调整分包预算" />
          </Form.Item>
        </Space>
      </Card>

      <Divider orientation="left">进度与状态</Divider>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        进度为人工维护值，用于经营跟踪，不代表系统自动计算的施工完成度。
      </Text>

      <Form.Item
        label="手动进度（%）"
        name="progress"
        tooltip="该进度为人工维护值，不代表系统自动计算的施工完成度"
      >
        <Row gutter={isMobile ? 12 : 16}>
          <Col xs={24} sm={18}>
            <Slider
              min={0}
              max={100}
              marks={isMobile ? {
                0: '0%',
                50: '50%',
                100: '100%'
              } : {
                0: '0%',
                25: '25%',
                50: '50%',
                75: '75%',
                100: '100%'
              }}
              onChange={(value) => form.setFieldsValue({ progress: value })}
            />
          </Col>
          <Col xs={24} sm={6}>
            <InputNumber
              min={0}
              max={100}
              style={{ width: '100%' }}
              formatter={value => `${value}%`}
              parser={value => value!.replace('%', '')}
              size={isMobile ? 'large' : 'middle'}
            />
          </Col>
        </Row>
      </Form.Item>

      {project && (
        <Form.Item
          label="项目状态"
          name="status"
          rules={[
            { required: true, message: '请选择项目状态' },
          ]}
        >
          <Select
            placeholder="请选择项目状态"
            size={isMobile ? 'large' : 'middle'}
          >
            <Option value={ProjectStatus.ACTIVE}>进行中</Option>
            <Option value={ProjectStatus.COMPLETED}>已完成</Option>
            <Option value={ProjectStatus.PAUSED}>已暂停</Option>
          </Select>
        </Form.Item>
      )}
    </MobileForm>
  );
};

export default ProjectForm;
